import { createFileRoute, Link } from "@tanstack/react-router";
import { JsonLd } from "~/components/seo/JsonLd";
import { schemaGraph, webPageSchema, breadcrumbSchema } from "~/lib/seo/schema";
import {
  din5008HowToSchema,
  din5008FaqSchema,
  DIN5008_HOWTO_STEPS,
  DIN5008_FAQS,
} from "~/lib/seo/geo-din5008";
import { DIN5008_SPEC } from "~/lib/resume/din5008-spec";
import { DIN5008_LANDING_PATH } from "~/lib/seo/public-landing-routes";

export const DIN5008_PAGE_URL = `https://alchema.eu${DIN5008_LANDING_PATH}` as const;
export const DIN5008_PAGE_TITLE =
  "DIN 5008 Lebenslauf-Generator — tabellarischer Lebenslauf | Alchema";
export const DIN5008_PAGE_DESCRIPTION =
  "Erstelle einen tabellarischen Lebenslauf mit DIN-5008-Form-B-Rändern, " +
  "lückenlosem Werdegang und einem klaren, einspaltigen Aufbau.";

export const Route = createFileRoute("/de/lebenslauf-generator-din-5008")({
  head: () => ({
    meta: [
      { title: DIN5008_PAGE_TITLE },
      { name: "description", content: DIN5008_PAGE_DESCRIPTION },
      { property: "og:title", content: DIN5008_PAGE_TITLE },
      { property: "og:description", content: DIN5008_PAGE_DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: DIN5008_PAGE_URL },
      { property: "og:locale", content: "de_DE" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "index, follow" },
    ],
    links: [{ rel: "canonical", href: DIN5008_PAGE_URL }],
  }),
  component: Din5008GeneratorPage,
});

function Din5008GeneratorPage() {
  const { margins, gapThresholdMonths } = DIN5008_SPEC;

  // De-duplicated JSON-LD: the page copy and the structured data share the same
  // spec-derived source (geo-din5008.ts).
  const jsonLd = schemaGraph(
    webPageSchema({
      name: DIN5008_PAGE_TITLE,
      description: DIN5008_PAGE_DESCRIPTION,
      url: DIN5008_PAGE_URL,
    }),
    breadcrumbSchema([
      { name: "Start", url: "/" },
      { name: "DIN 5008 Lebenslauf-Generator", url: DIN5008_LANDING_PATH },
    ]),
    din5008HowToSchema(),
    din5008FaqSchema(),
  );

  return (
    <div style={{ backgroundColor: "var(--bg)" }}>
      <JsonLd json={jsonLd} />
      <section className="mx-auto max-w-3xl px-6 pb-24 pt-20" style={{ color: "var(--text)" }}>
        <h1 className="text-3xl font-bold tracking-tight" style={{ color: "var(--text)" }}>
          DIN 5008 Lebenslauf-Generator
        </h1>
        <p className="mt-4 text-base" style={{ color: "var(--text-secondary)" }}>
          Erstelle einen tabellarischen Lebenslauf nach DIN 5008 Form B — mit korrekten
          Seitenrändern ({margins.topMm}mm oben, {margins.leftMm}mm links, {margins.rightMm}mm
          rechts, {margins.bottomMm}mm unten), lückenlosem Werdegang und einem klaren,
          einspaltigen Aufbau für Menschen und gängige Bewerbungssoftware.
        </p>

        {/* How-to */}
        <h2 className="mt-12 text-xl font-semibold" style={{ color: "var(--text)" }}>
          In vier Schritten zum DIN-5008-Lebenslauf
        </h2>
        <ol className="mt-6 space-y-5">
          {DIN5008_HOWTO_STEPS.map((step, i) => (
            <li key={step.name} className="flex gap-4">
              <span
                className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white"
                style={{ background: "var(--accent)" }}
              >
                {i + 1}
              </span>
              <div>
                <h3 className="text-base font-semibold" style={{ color: "var(--text)" }}>
                  {step.name}
                </h3>
                <p className="mt-1 text-sm" style={{ color: "var(--text-secondary)" }}>
                  {step.text}
                </p>
              </div>
            </li>
          ))}
        </ol>

        {/* Lückenlosigkeit note */}
        <p className="mt-8 text-sm" style={{ color: "var(--text-secondary)" }}>
          DACH-Personaler:innen erwarten einen lückenlosen Lebenslauf: Zeiträume von mehr als{" "}
          {gapThresholdMonths} Monaten zwischen zwei Stationen sollten erläutert werden.
        </p>

        {/* FAQ */}
        <h2 className="mt-12 text-xl font-semibold" style={{ color: "var(--text)" }}>
          Häufige Fragen zu DIN 5008
        </h2>
        <dl className="mt-6 space-y-6">
          {DIN5008_FAQS.map((faq) => (
            <div key={faq.question}>
              <dt className="text-base font-semibold" style={{ color: "var(--text)" }}>
                {faq.question}
              </dt>
              <dd className="mt-1 text-sm" style={{ color: "var(--text-secondary)" }}>
                {faq.answer}
              </dd>
            </div>
          ))}
        </dl>

        <div className="mt-12">
          <Link
            to="/register"
            className="inline-flex rounded-[10px] px-5 py-2.5 text-sm font-medium text-white"
            style={{ background: "var(--accent)" }}
          >
            Lebenslauf erstellen
          </Link>
        </div>
      </section>
    </div>
  );
}
